import {
  isTaskTypeViewFieldType,
  isTaskTypeViewSourceField,
  isTaskTypeViewSourcePlacement,
  isTaskTypeViewSupplierField,
  TASK_TYPE_VIEW_MATCH_FIELD_TYPES,
  type SystemEntity,
  type TaskTypeView,
  type TaskTypeViewMatchTarget,
  type TaskTypeViewPlacement,
  type TaskTypeViewRegionColumn,
  type TaskTypeViewSourceKind,
} from '@erp/shared-types';
import { parseViewDraft } from './taskTypeViewDraft';

/** Caps mirrored from `TaskTypeViewSchema`. The editor stops before Zod rejects. */
export const MAX_VIEW_REGIONS = 20;
export const MAX_VIEW_PLACEMENTS = 50;
export const MAX_VIEW_COLUMNS = 12;

const MATCH_FIELD_TYPES = new Set<string>(TASK_TYPE_VIEW_MATCH_FIELD_TYPES);

export type ViewSchemaField = {
  fieldName: string;
  fieldType: string;
  label: string;
  isMultiple?: boolean;
  referencedEntityType?: SystemEntity | null;
  children?: ViewSchemaField[];
};

export type ViewSchemaWireField = {
  id?: string;
  fieldName: string;
  fieldType: string;
  label: string;
  isMultiple?: boolean;
  referencedEntityType?: SystemEntity | null;
  parentFieldId?: string | null;
  children?: ViewSchemaWireField[];
};

export type ViewFieldOption = {
  path: string[];
  label: string;
  missing?: boolean;
};

export type ViewSchemaSource =
  | { status: 'none' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; fields: ViewSchemaField[] };

export type ViewMoveDirection = -1 | 1;

export type TaskPageDraft =
  | { status: 'standard' }
  | { status: 'invalid' }
  | { status: 'ready'; view: TaskTypeView };

type PlacementKind = 'field' | 'match' | 'lineList';
type ColumnKind = 'field' | 'match';
type ViewColumn = Extract<TaskTypeViewPlacement, { component: ColumnKind }>;

export function resolveViewSchemaSource(input: {
  selectedId: string;
  noneId: string;
  saved: { id: string; fields: readonly ViewSchemaWireField[] } | null;
  fetched: { fields: readonly ViewSchemaWireField[] } | null;
  fetchFailed: boolean;
}): ViewSchemaSource {
  if (input.selectedId === input.noneId) return { status: 'none' };
  if (input.saved?.id === input.selectedId)
    return readySchema(input.saved.fields);
  return fetchedSchema(input.fetched, input.fetchFailed);
}

function readySchema(fields: readonly ViewSchemaWireField[]): ViewSchemaSource {
  return { status: 'ready', fields: viewSchemaFieldsFromWire(fields) };
}

function fetchedSchema(
  fetched: { fields: readonly ViewSchemaWireField[] } | null,
  fetchFailed: boolean,
): ViewSchemaSource {
  if (fetchFailed) return { status: 'error' };
  if (!fetched) return { status: 'loading' };
  return readySchema(fetched.fields);
}

export function viewSchemaFieldsFromWire(
  fields: readonly ViewSchemaWireField[],
): ViewSchemaField[] {
  if (fields.some((field) => field.parentFieldId)) {
    return nestWireFields(fields);
  }
  return fields.map(viewSchemaFieldFromNode);
}

export function readTaskPageDraft(text: string): TaskPageDraft {
  const parsed = parseViewDraft(text);
  if (!parsed.ok) return { status: 'invalid' };
  if (!parsed.view) return { status: 'standard' };
  return { status: 'ready', view: parsed.view };
}

export function writeTaskPageDraft(view: TaskTypeView | null): string {
  if (!view) return '';
  return JSON.stringify(view, null, 2);
}

export function startTaskPage(): TaskTypeView {
  return (
    addViewRegion({ version: 1, regions: [] }) ?? { version: 1, regions: [] }
  );
}

export function listSupplierLineOptions(
  fields: readonly ViewSchemaField[],
): ViewFieldOption[] {
  const options: ViewFieldOption[] = [];
  collectSupplierLines(fields, [], options);
  return options;
}

export function listViewFieldOptions(
  fields: readonly ViewSchemaField[],
  kind: 'scalar' | 'match' | 'lineList',
): ViewFieldOption[] {
  const options: ViewFieldOption[] = [];
  if (kind === 'lineList') collectLineLists(fields, [], options);
  else if (kind === 'match') collectMatches(fields, [], options);
  else collectScalars(fields, [], options);
  return options;
}

/** Fields a supplier-line remap may read its supplier from. */
export function listViewSupplierOptions(
  fields: readonly ViewSchemaField[],
): ViewFieldOption[] {
  const options: ViewFieldOption[] = [];
  collectSuppliers(fields, [], options);
  return options;
}

/** Fields an email or document placement may read its id from. */
export function listViewSourceOptions(
  fields: readonly ViewSchemaField[],
  kind: TaskTypeViewSourceKind,
): ViewFieldOption[] {
  const options: ViewFieldOption[] = [];
  collectSources(kind, fields, [], options);
  return options;
}

export function listColumnOptions(
  fields: readonly ViewSchemaField[],
  lineListPath: readonly string[],
  kind: 'scalar' | 'match',
): ViewFieldOption[] {
  const lineList = findViewSchemaField(fields, lineListPath);
  if (!lineList) return [];
  return listViewFieldOptions(lineList.children ?? [], kind);
}

export function withCurrentPath(
  options: readonly ViewFieldOption[],
  path: readonly string[],
): ViewFieldOption[] {
  if (options.some((option) => samePath(option.path, path)))
    return [...options];
  return [
    { path: [...path], label: path.join('.'), missing: true },
    ...options,
  ];
}

export function optionValue(path: readonly string[]): string {
  return JSON.stringify(path);
}

export function pathFromOptionValue(value: string): string[] | null {
  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    if (!parsed.every((segment) => typeof segment === 'string' && segment)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function viewHasPathIssues(
  view: TaskTypeView,
  fields: readonly ViewSchemaField[],
): boolean {
  return view.regions.some((region) =>
    region.placements.some((placement) => placementHasIssue(placement, fields)),
  );
}

export function addViewRegion(view: TaskTypeView): TaskTypeView | null {
  if (view.regions.length >= MAX_VIEW_REGIONS) return null;
  return {
    ...view,
    regions: [
      ...view.regions,
      {
        id: nextViewId(view, 'section'),
        title: null,
        column: 'main',
        placements: [],
      },
    ],
  };
}

export function setViewRegionTitle(
  view: TaskTypeView,
  regionId: string,
  title: string,
): TaskTypeView {
  const nextTitle = normalizeOptionalText(title);
  return {
    ...view,
    regions: view.regions.map((region) => {
      if (region.id !== regionId || region.title === nextTitle) return region;
      return { ...region, title: nextTitle };
    }),
  };
}

export function setViewRegionColumn(
  view: TaskTypeView,
  regionId: string,
  column: TaskTypeViewRegionColumn,
): TaskTypeView {
  return {
    ...view,
    regions: view.regions.map((region) => {
      if (region.id !== regionId || region.column === column) return region;
      return { ...region, column };
    }),
  };
}

export function removeViewRegion(
  view: TaskTypeView,
  regionId: string,
): TaskTypeView {
  if (!view.regions.some((region) => region.id === regionId)) return view;
  return {
    ...view,
    regions: view.regions.filter((region) => region.id !== regionId),
  };
}

export function moveViewRegion(
  view: TaskTypeView,
  regionId: string,
  direction: ViewMoveDirection,
): TaskTypeView {
  const regions = moveById(view.regions, regionId, direction);
  if (!regions) return view;
  return { ...view, regions };
}

export function addViewSource(
  view: TaskTypeView,
  regionId: string,
  component: TaskTypeViewSourceKind,
): TaskTypeView | null {
  const region = view.regions.find((item) => item.id === regionId);
  if (!region || region.placements.length >= MAX_VIEW_PLACEMENTS) return null;
  const placement: TaskTypeViewPlacement = {
    id: nextViewId(view, component),
    component,
    config: { label: null, source: { from: 'anchor' } },
  };
  return replaceRegion(view, regionId, {
    ...region,
    placements: [...region.placements, placement],
  });
}

export type RegionAddKind =
  | 'field'
  | 'match'
  | 'lineList'
  | 'supplierLines'
  | 'email'
  | 'document'
  | 'apInvoice'
  | 'cashReceipt';

export type RegionAddAction = {
  kind: RegionAddKind;
  enabled: boolean;
  next: TaskTypeView | null;
};

/** What each section add button would place, and whether the schema allows it. */
export function regionAddActions(
  view: TaskTypeView,
  regionId: string,
  fields: readonly ViewSchemaField[],
  fieldsReady: boolean,
): RegionAddAction[] {
  const region = view.regions.find((item) => item.id === regionId);
  const atCap =
    region == null || region.placements.length >= MAX_VIEW_PLACEMENTS;
  return [
    regionAddAction(
      'field',
      canPlaceKind(fields, 'scalar', fieldsReady, atCap),
      addViewPlacement(view, regionId, 'field', fields),
    ),
    regionAddAction(
      'match',
      canPlaceKind(fields, 'match', fieldsReady, atCap),
      addViewPlacement(view, regionId, 'match', fields),
    ),
    regionAddAction(
      'lineList',
      canPlaceKind(fields, 'lineList', fieldsReady, atCap),
      addViewPlacement(view, regionId, 'lineList', fields),
    ),
    regionAddAction(
      'supplierLines',
      canPlaceSupplierLines(fields, fieldsReady, atCap),
      addViewSupplierLines(view, regionId, fields),
    ),
    regionAddAction('email', !atCap, addViewSource(view, regionId, 'email')),
    regionAddAction(
      'document',
      !atCap,
      addViewSource(view, regionId, 'document'),
    ),
    regionAddAction(
      'apInvoice',
      !atCap,
      addViewSource(view, regionId, 'apInvoice'),
    ),
    regionAddAction(
      'cashReceipt',
      !atCap,
      addViewSource(view, regionId, 'cashReceipt'),
    ),
  ];
}

/**
 * True when the schema has a collection but none a remap can be placed on:
 * every array or json field declares children, so a correction save would
 * reject the match row's own keys. The builder explains this instead of
 * leaving Remap Lines disabled without a reason.
 */
export function supplierLinesBlockedByChildren(
  fields: readonly ViewSchemaField[],
): boolean {
  if (listSupplierLineOptions(fields).length > 0) return false;
  return listViewFieldOptions(fields, 'lineList').some((option) => {
    const field = findViewSchemaField(fields, option.path);
    return (
      field != null &&
      (field.fieldType === 'array' || field.fieldType === 'json') &&
      (field.children?.length ?? 0) > 0
    );
  });
}

function canPlaceSupplierLines(
  fields: readonly ViewSchemaField[],
  fieldsReady: boolean,
  atCap: boolean,
): boolean {
  if (!fieldsReady || atCap) return false;
  return listSupplierLineOptions(fields).length > 0;
}

function canPlaceKind(
  fields: readonly ViewSchemaField[],
  kind: 'scalar' | 'match' | 'lineList',
  fieldsReady: boolean,
  atCap: boolean,
): boolean {
  if (!fieldsReady || atCap) return false;
  return listViewFieldOptions(fields, kind).length > 0;
}

function regionAddAction(
  kind: RegionAddKind,
  enabled: boolean,
  next: TaskTypeView | null,
): RegionAddAction {
  return { kind, enabled, next: enabled ? next : null };
}

export function addViewSupplierLines(
  view: TaskTypeView,
  regionId: string,
  fields: readonly ViewSchemaField[],
): TaskTypeView | null {
  const region = view.regions.find((item) => item.id === regionId);
  if (!region || region.placements.length >= MAX_VIEW_PLACEMENTS) return null;
  const first = listSupplierLineOptions(fields)[0];
  if (!first) return null;
  const supplier = defaultSupplierOption(fields);
  const placement: TaskTypeViewPlacement = {
    id: nextViewId(view, 'supplier-lines'),
    component: 'supplierLineMatch',
    config: {
      label: null,
      path: [...first.path],
      supplierPath: supplier ? [...supplier.path] : null,
      columnSchemaDefinitionId: null,
      columns: [],
    },
  };
  return replaceRegion(view, regionId, {
    ...region,
    placements: [...region.placements, placement],
  });
}

/** A typed supplier reference first; a bare string id only when nothing better exists. */
function defaultSupplierOption(
  fields: readonly ViewSchemaField[],
): ViewFieldOption | undefined {
  const options = listViewSupplierOptions(fields);
  const typed = options.find((option) => {
    const field = findViewSchemaField(fields, option.path);
    return field?.fieldType === 'entity_reference';
  });
  return typed ?? options[0];
}

export function setSupplierLineSupplierPath(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  path: readonly string[] | null,
): TaskTypeView {
  if (path && !isViewPath(path)) return view;
  return mapPlacement(view, regionId, placementId, (placement) => {
    if (placement.component !== 'supplierLineMatch') return placement;
    const current = placement.config.supplierPath;
    if (current === null && path === null) return placement;
    if (current && path && samePath(current, path)) return placement;
    return {
      ...placement,
      config: { ...placement.config, supplierPath: path ? [...path] : null },
    };
  });
}

type SupplierLinePlacement = Extract<
  TaskTypeViewPlacement,
  { component: 'supplierLineMatch' }
>;
type SupplierLineColumn = SupplierLinePlacement['config']['columns'][number];

/** Match rows keep schema-declared columns under `extracted`. */
const DEFAULT_SUPPLIER_LINE_COLUMN_PATH = ['extracted', 'quantity'];

/** Dotted text for a typed column path. */
export function supplierLineColumnPathText(path: readonly string[]): string {
  return path.join('.');
}

/**
 * Column path from dotted text. Null when a segment is empty or the path is
 * too long, so the control keeps the previous path instead of saving junk.
 */
export function supplierLineColumnPathFromText(text: string): string[] | null {
  const segments = text.split('.').map((segment) => segment.trim());
  if (segments.some((segment) => segment === '')) return null;
  return isViewPath(segments) ? segments : null;
}

/**
 * Keys every match row carries besides `extracted`. Offered after the
 * extraction schema's own fields so an author can show the match itself.
 */
export const MATCH_ROW_COLUMN_OPTIONS: readonly ViewFieldOption[] = [
  { path: ['matchLayer'], label: 'Match layer' },
  { path: ['confidence'], label: 'Confidence' },
  { path: ['matchReason'], label: 'Match reason' },
  { path: ['identifiers', 'sku'], label: 'Identifier: SKU' },
  { path: ['identifiers', 'upc'], label: 'Identifier: UPC' },
  { path: ['identifiers', 'description'], label: 'Identifier: description' },
];

/**
 * Column suggestions for supplier lines from the extraction schema the match
 * node was pinned to. The node copies each row of the schema's line-item
 * array onto `extracted`, so every child of a top-level collection becomes
 * `extracted.<child>`; a flat schema's own scalars are offered the same way.
 * The fixed match-row keys follow.
 */
export function supplierLineColumnSuggestions(
  extractionFields: readonly ViewSchemaField[],
): ViewFieldOption[] {
  const rowFields = extractionFields.flatMap((field) =>
    isLineListField(field) ? (field.children ?? []) : [],
  );
  const source = rowFields.length > 0 ? rowFields : extractionFields;
  const extracted: ViewFieldOption[] = [];
  for (const field of source) {
    if (field.children?.length) continue;
    extracted.push({
      path: ['extracted', field.fieldName],
      label: field.label || field.fieldName,
    });
  }
  return [...extracted, ...MATCH_ROW_COLUMN_OPTIONS];
}

export function setSupplierLineColumnSchema(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  columnSchemaDefinitionId: string | null,
): TaskTypeView {
  return mapPlacement(view, regionId, placementId, (placement) => {
    if (placement.component !== 'supplierLineMatch') return placement;
    if (
      placement.config.columnSchemaDefinitionId === columnSchemaDefinitionId
    ) {
      return placement;
    }
    return {
      ...placement,
      config: { ...placement.config, columnSchemaDefinitionId },
    };
  });
}

/**
 * Add one column: the first suggestion not already placed, with its label;
 * otherwise a typed default path the author edits.
 */
export function addSupplierLineColumn(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  suggestions: readonly ViewFieldOption[] = [],
): TaskTypeView | null {
  const placement = findPlacement(view, regionId, placementId);
  if (!placement || placement.component !== 'supplierLineMatch') return null;
  if (placement.config.columns.length >= MAX_VIEW_COLUMNS) return null;
  const unused = unusedSuggestions(placement.config.columns, suggestions)[0];
  const column: SupplierLineColumn = {
    id: nextViewId(view, 'column'),
    label: unused ? unused.label : null,
    path: unused ? [...unused.path] : [...DEFAULT_SUPPLIER_LINE_COLUMN_PATH],
  };
  return mapSupplierLineColumns(view, regionId, placementId, (columns) => [
    ...columns,
    column,
  ]);
}

/**
 * Add every `extracted.*` suggestion not already placed, up to the cap.
 * Null when nothing would change.
 */
export function addAllExtractedSupplierLineColumns(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  suggestions: readonly ViewFieldOption[],
): TaskTypeView | null {
  const placement = findPlacement(view, regionId, placementId);
  if (!placement || placement.component !== 'supplierLineMatch') return null;
  const room = MAX_VIEW_COLUMNS - placement.config.columns.length;
  const additions = unusedSuggestions(placement.config.columns, suggestions)
    .filter((option) => option.path[0] === 'extracted')
    .slice(0, Math.max(room, 0));
  if (additions.length === 0) return null;
  let next = view;
  for (const option of additions) {
    const column: SupplierLineColumn = {
      id: nextViewId(next, 'column'),
      label: option.label,
      path: [...option.path],
    };
    next = mapSupplierLineColumns(next, regionId, placementId, (columns) => [
      ...columns,
      column,
    ]);
  }
  return next;
}

function unusedSuggestions(
  columns: readonly SupplierLineColumn[],
  suggestions: readonly ViewFieldOption[],
): ViewFieldOption[] {
  return suggestions.filter(
    (option) => !columns.some((column) => samePath(column.path, option.path)),
  );
}

export function removeSupplierLineColumn(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  columnId: string,
): TaskTypeView {
  return mapSupplierLineColumns(view, regionId, placementId, (columns) =>
    removeById(columns, columnId),
  );
}

export function moveSupplierLineColumn(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  columnId: string,
  direction: ViewMoveDirection,
): TaskTypeView {
  return mapSupplierLineColumns(view, regionId, placementId, (columns) => {
    return moveById(columns, columnId, direction) ?? columns;
  });
}

export function setSupplierLineColumnPath(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  columnId: string,
  path: readonly string[],
): TaskTypeView {
  if (!isViewPath(path)) return view;
  return mapSupplierLineColumn(view, regionId, placementId, columnId, (col) => {
    if (samePath(col.path, path)) return col;
    return { ...col, path: [...path] };
  });
}

export function setSupplierLineColumnLabel(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  columnId: string,
  label: string,
): TaskTypeView {
  const next = normalizeOptionalText(label);
  return mapSupplierLineColumn(view, regionId, placementId, columnId, (col) => {
    if (col.label === next) return col;
    return { ...col, label: next };
  });
}

function mapSupplierLineColumns(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  update: (
    columns: SupplierLinePlacement['config']['columns'],
  ) => SupplierLinePlacement['config']['columns'],
): TaskTypeView {
  return mapPlacement(view, regionId, placementId, (placement) => {
    if (placement.component !== 'supplierLineMatch') return placement;
    const columns = update(placement.config.columns);
    if (columns === placement.config.columns) return placement;
    return { ...placement, config: { ...placement.config, columns } };
  });
}

function mapSupplierLineColumn(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  columnId: string,
  update: (column: SupplierLineColumn) => SupplierLineColumn,
): TaskTypeView {
  return mapSupplierLineColumns(view, regionId, placementId, (columns) =>
    updateById(columns, columnId, update),
  );
}

export function setViewSourceMode(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  from: 'anchor' | 'path',
  fields: readonly ViewSchemaField[],
): TaskTypeView {
  return mapPlacement(view, regionId, placementId, (placement) => {
    if (!isTaskTypeViewSourcePlacement(placement)) return placement;
    if (from === 'anchor') {
      if (placement.config.source.from === 'anchor') return placement;
      return {
        ...placement,
        config: { ...placement.config, source: { from: 'anchor' } },
      };
    }
    if (placement.config.source.from === 'path') return placement;
    const first = listViewSourceOptions(fields, placement.component)[0];
    if (!first) return placement;
    return {
      ...placement,
      config: {
        ...placement.config,
        source: { from: 'path', path: [...first.path] },
      },
    };
  });
}

export function setViewSourcePath(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  path: readonly string[],
): TaskTypeView {
  if (!isViewPath(path)) return view;
  return mapPlacement(view, regionId, placementId, (placement) => {
    if (!isTaskTypeViewSourcePlacement(placement)) return placement;
    if (placement.config.source.from !== 'path') return placement;
    if (samePath(placement.config.source.path, path)) return placement;
    return {
      ...placement,
      config: {
        ...placement.config,
        source: { from: 'path', path: [...path] },
      },
    };
  });
}

export function addViewPlacement(
  view: TaskTypeView,
  regionId: string,
  component: PlacementKind,
  fields: readonly ViewSchemaField[],
): TaskTypeView | null {
  const region = view.regions.find((item) => item.id === regionId);
  if (!region || region.placements.length >= MAX_VIEW_PLACEMENTS) return null;
  const kind = component === 'field' ? 'scalar' : component;
  const first = listViewFieldOptions(fields, kind)[0];
  if (!first) return null;
  const placement = createPlacement(view, component, first.path);
  return replaceRegion(view, regionId, {
    ...region,
    placements: [...region.placements, placement],
  });
}

export function removeViewPlacement(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
): TaskTypeView {
  return mapRegionPlacements(view, regionId, (placements) =>
    removeById(placements, placementId),
  );
}

export function moveViewPlacement(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  direction: ViewMoveDirection,
): TaskTypeView {
  return mapRegionPlacements(view, regionId, (placements) => {
    return moveById(placements, placementId, direction) ?? placements;
  });
}

export function setViewPlacementPath(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  path: readonly string[],
): TaskTypeView {
  if (!isViewPath(path)) return view;
  return mapPlacement(view, regionId, placementId, (placement) => {
    if (isTaskTypeViewSourcePlacement(placement)) return placement;
    if (samePath(placement.config.path, path)) return placement;
    return withPath(placement, path);
  });
}

export function setViewPlacementLabel(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  label: string,
): TaskTypeView {
  const next = normalizeOptionalText(label);
  return mapPlacement(view, regionId, placementId, (placement) => {
    if (placement.config.label === next) return placement;
    return withLabel(placement, next);
  });
}

export function setViewPlacementMode(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  mode: 'read' | 'edit',
): TaskTypeView {
  return mapPlacement(view, regionId, placementId, (placement) => {
    if (placement.component !== 'field' && placement.component !== 'match') {
      return placement;
    }
    if (placement.config.mode === mode) return placement;
    return withMode(placement, mode);
  });
}

export function setViewMatchTarget(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  target: TaskTypeViewMatchTarget,
): TaskTypeView {
  return mapPlacement(view, regionId, placementId, (placement) => {
    if (placement.component !== 'match') return placement;
    if (placement.config.target === target) return placement;
    return {
      ...placement,
      config: { ...placement.config, target },
    };
  });
}

export function addViewColumn(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  component: ColumnKind,
  fields: readonly ViewSchemaField[],
): TaskTypeView | null {
  const placement = findPlacement(view, regionId, placementId);
  if (!placement || placement.component !== 'lineList') return null;
  if (placement.config.columns.length >= MAX_VIEW_COLUMNS) return null;
  const first = listColumnOptions(
    fields,
    placement.config.path,
    component === 'field' ? 'scalar' : 'match',
  )[0];
  if (!first) return null;
  const column = createColumn(view, component, first.path);
  return mapPlacement(view, regionId, placementId, (current) => {
    if (current.component !== 'lineList') return current;
    return {
      ...current,
      config: {
        ...current.config,
        columns: [...current.config.columns, column],
      },
    };
  });
}

export function removeViewColumn(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  columnId: string,
): TaskTypeView {
  return mapColumns(view, regionId, placementId, (columns) =>
    removeById(columns, columnId),
  );
}

export function moveViewColumn(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  columnId: string,
  direction: ViewMoveDirection,
): TaskTypeView {
  return mapColumns(view, regionId, placementId, (columns) => {
    return moveById(columns, columnId, direction) ?? columns;
  });
}

export function setViewColumnPath(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  columnId: string,
  path: readonly string[],
): TaskTypeView {
  if (!isViewPath(path)) return view;
  return mapColumn(view, regionId, placementId, columnId, (column) => {
    if (samePath(column.config.path, path)) return column;
    return withColumnPath(column, path);
  });
}

export function setViewColumnLabel(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  columnId: string,
  label: string,
): TaskTypeView {
  const next = normalizeOptionalText(label);
  return mapColumn(view, regionId, placementId, columnId, (column) => {
    if (column.config.label === next) return column;
    return withColumnLabel(column, next);
  });
}

export function setViewColumnMode(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  columnId: string,
  mode: 'read' | 'edit',
): TaskTypeView {
  return mapColumn(view, regionId, placementId, columnId, (column) => {
    if (column.config.mode === mode) return column;
    return withColumnMode(column, mode);
  });
}

export function setViewColumnTarget(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  columnId: string,
  target: TaskTypeViewMatchTarget,
): TaskTypeView {
  return mapColumn(view, regionId, placementId, columnId, (column) => {
    if (column.component !== 'match' || column.config.target === target) {
      return column;
    }
    return { ...column, config: { ...column.config, target } };
  });
}

function nestWireFields(
  fields: readonly ViewSchemaWireField[],
): ViewSchemaField[] {
  const childrenOf = new Map<string, ViewSchemaWireField[]>();
  for (const field of fields) {
    if (!field.parentFieldId || !field.id) continue;
    const siblings = childrenOf.get(field.parentFieldId) ?? [];
    siblings.push(field);
    childrenOf.set(field.parentFieldId, siblings);
  }
  const roots: ViewSchemaField[] = [];
  for (const field of fields) {
    if (field.parentFieldId) continue;
    roots.push(nestWireNode(field, childrenOf));
  }
  return roots;
}

function nestWireNode(
  field: ViewSchemaWireField,
  childrenOf: ReadonlyMap<string, readonly ViewSchemaWireField[]>,
): ViewSchemaField {
  const nested = field.id ? (childrenOf.get(field.id) ?? []) : [];
  const children = nested.map((child) => nestWireNode(child, childrenOf));
  return viewSchemaNode(field, children);
}

function viewSchemaFieldFromNode(field: ViewSchemaWireField): ViewSchemaField {
  const children = (field.children ?? []).map(viewSchemaFieldFromNode);
  return viewSchemaNode(field, children);
}

function viewSchemaNode(
  field: ViewSchemaWireField,
  children: ViewSchemaField[],
): ViewSchemaField {
  const node: ViewSchemaField = {
    fieldName: field.fieldName,
    fieldType: field.fieldType,
    label: field.label,
    isMultiple: field.isMultiple === true,
    ...(field.referencedEntityType
      ? { referencedEntityType: field.referencedEntityType }
      : {}),
  };
  if (children.length === 0) return node;
  return { ...node, children };
}

function collectScalars(
  fields: readonly ViewSchemaField[],
  prefix: readonly string[],
  options: ViewFieldOption[],
): void {
  for (const field of fields) {
    const path = [...prefix, field.fieldName];
    if (isLineListField(field)) continue;
    if (isScalarField(field)) options.push(fieldOption(field, path));
    else walkChildren(field, path, options, collectScalars);
  }
}

function collectMatches(
  fields: readonly ViewSchemaField[],
  prefix: readonly string[],
  options: ViewFieldOption[],
): void {
  for (const field of fields) {
    const path = [...prefix, field.fieldName];
    if (isLineListField(field)) continue;
    if (isMatchField(field)) options.push(fieldOption(field, path));
    else walkChildren(field, path, options, collectMatches);
  }
}

function collectSuppliers(
  fields: readonly ViewSchemaField[],
  prefix: readonly string[],
  options: ViewFieldOption[],
): void {
  collectMatching(isTaskTypeViewSupplierField, fields, prefix, options);
}

function collectSources(
  kind: TaskTypeViewSourceKind,
  fields: readonly ViewSchemaField[],
  prefix: readonly string[],
  options: ViewFieldOption[],
): void {
  collectMatching(
    (field) => isTaskTypeViewSourceField(kind, field),
    fields,
    prefix,
    options,
  );
}

/**
 * Scalar fields matching `accept`, anywhere outside a collection. Objects
 * are walked; arrays and multiple fields are skipped like the other option
 * collectors.
 */
function collectMatching(
  accept: (field: ViewSchemaField) => boolean,
  fields: readonly ViewSchemaField[],
  prefix: readonly string[],
  options: ViewFieldOption[],
): void {
  for (const field of fields) {
    const path = [...prefix, field.fieldName];
    if (isLineListField(field)) continue;
    if (accept(field)) {
      options.push(fieldOption(field, path));
    } else {
      walkChildren(field, path, options, (children, childPrefix, out) =>
        collectMatching(accept, children, childPrefix, out),
      );
    }
  }
}

function collectSupplierLines(
  fields: readonly ViewSchemaField[],
  prefix: readonly string[],
  options: ViewFieldOption[],
): void {
  for (const field of fields) {
    const path = [...prefix, field.fieldName];
    if (isSupplierLineField(field)) {
      options.push(fieldOption(field, path));
      continue;
    }
    // Match save-time checks: a path cannot step through an array or a
    // multiple field to reach a childless json blob underneath.
    if (field.fieldType === 'array' || field.isMultiple === true) continue;
    walkChildren(field, path, options, collectSupplierLines);
  }
}

function collectLineLists(
  fields: readonly ViewSchemaField[],
  prefix: readonly string[],
  options: ViewFieldOption[],
): void {
  for (const field of fields) {
    const path = [...prefix, field.fieldName];
    if (isLineListField(field)) {
      options.push(fieldOption(field, path));
      continue;
    }
    walkChildren(field, path, options, collectLineLists);
  }
}

function walkChildren(
  field: ViewSchemaField,
  path: readonly string[],
  options: ViewFieldOption[],
  collect: (
    fields: readonly ViewSchemaField[],
    prefix: readonly string[],
    options: ViewFieldOption[],
  ) => void,
): void {
  if (isScalarField(field) || !field.children?.length) return;
  collect(field.children, path, options);
}

function fieldOption(
  field: ViewSchemaField,
  path: readonly string[],
): ViewFieldOption {
  const own = field.label.trim() || field.fieldName;
  const titled =
    field.label.trim() && field.label.trim() !== field.fieldName
      ? `${own} (${field.fieldName})`
      : own;
  const label =
    path.length <= 1 ? titled : `${path.slice(0, -1).join(' / ')} / ${titled}`;
  return { path: [...path], label };
}

export function isSupplierLineField(field: ViewSchemaField): boolean {
  if ((field.children?.length ?? 0) > 0) return false;
  return field.fieldType === 'json' || field.fieldType === 'array';
}

export function isLineListField(field: ViewSchemaField): boolean {
  return (
    field.fieldType === 'array' ||
    field.fieldType === 'json' ||
    field.isMultiple === true
  );
}

function isScalarField(field: ViewSchemaField): boolean {
  return field.isMultiple !== true && isTaskTypeViewFieldType(field.fieldType);
}

function isMatchField(field: ViewSchemaField): boolean {
  return field.isMultiple !== true && MATCH_FIELD_TYPES.has(field.fieldType);
}

function findViewSchemaField(
  fields: readonly ViewSchemaField[],
  path: readonly string[],
): ViewSchemaField | null {
  let current = fields;
  let found: ViewSchemaField | undefined;
  for (const [index, segment] of path.entries()) {
    found = current.find((field) => field.fieldName === segment);
    if (!found) return null;
    const isLast = index === path.length - 1;
    if (!isLast && (found.fieldType === 'array' || found.isMultiple === true)) {
      return null;
    }
    current = found.children ?? [];
  }
  return found ?? null;
}

function placementHasIssue(
  placement: TaskTypeViewPlacement,
  fields: readonly ViewSchemaField[],
): boolean {
  if (isTaskTypeViewSourcePlacement(placement)) {
    return sourcePathIssue(
      placement.component,
      placement.config.source,
      fields,
    );
  }
  return valuePlacementIssue(placement, fields);
}

function valuePlacementIssue(
  placement: Exclude<
    TaskTypeViewPlacement,
    { component: TaskTypeViewSourceKind }
  >,
  fields: readonly ViewSchemaField[],
): boolean {
  const resolved = findViewSchemaField(fields, placement.config.path);
  if (!resolved) return true;
  if (placement.component === 'supplierLineMatch') {
    return (
      !isSupplierLineField(resolved) ||
      supplierPathIssue(placement.config.supplierPath, fields)
    );
  }
  if (placement.component === 'lineList')
    return lineListIssue(placement, resolved);
  if (placement.component === 'match') return !isMatchField(resolved);
  return !isScalarField(resolved);
}

function supplierPathIssue(
  supplierPath: readonly string[] | null,
  fields: readonly ViewSchemaField[],
): boolean {
  if (!supplierPath) return false;
  const resolved = findViewSchemaField(fields, supplierPath);
  return !resolved || !isTaskTypeViewSupplierField(resolved);
}

function lineListIssue(
  placement: Extract<TaskTypeViewPlacement, { component: 'lineList' }>,
  resolved: ViewSchemaField,
): boolean {
  if (!isLineListField(resolved)) return true;
  const columns = resolved.children ?? [];
  if (columns.length === 0) return false;
  return placement.config.columns.some((column) =>
    columnHasIssue(column, columns),
  );
}

function sourcePathIssue(
  kind: TaskTypeViewSourceKind,
  source: { from: 'anchor' } | { from: 'path'; path: string[] },
  fields: readonly ViewSchemaField[],
): boolean {
  if (source.from === 'anchor') return false;
  const resolved = findViewSchemaField(fields, source.path);
  return !resolved || !isTaskTypeViewSourceField(kind, resolved);
}

function columnHasIssue(
  column: Extract<TaskTypeViewPlacement, { component: 'field' | 'match' }>,
  fields: readonly ViewSchemaField[],
): boolean {
  const resolved = findViewSchemaField(fields, column.config.path);
  if (!resolved) return true;
  if (column.component === 'match') return !isMatchField(resolved);
  return !isScalarField(resolved);
}

function createPlacement(
  view: TaskTypeView,
  component: PlacementKind,
  path: readonly string[],
): TaskTypeViewPlacement {
  const nextPath = [...path];
  if (component === 'field') {
    return {
      id: nextViewId(view, 'field'),
      component: 'field',
      config: { label: null, path: nextPath, mode: 'edit' },
    };
  }
  if (component === 'match') {
    return {
      id: nextViewId(view, 'match'),
      component: 'match',
      config: {
        label: null,
        path: nextPath,
        target: 'supplier',
        mode: 'edit',
      },
    };
  }
  return {
    id: nextViewId(view, 'lines'),
    component: 'lineList',
    config: { label: null, path: nextPath, columns: [] },
  };
}

function nextViewId(view: TaskTypeView, prefix: string): string {
  const ids = collectIds(view);
  let n = 1;
  while (ids.has(`${prefix}-${n}`)) n += 1;
  return `${prefix}-${n}`;
}

function collectIds(view: TaskTypeView): Set<string> {
  const ids = new Set<string>();
  for (const region of view.regions) {
    ids.add(region.id);
    for (const placement of region.placements) {
      ids.add(placement.id);
      if (
        placement.component !== 'lineList' &&
        placement.component !== 'supplierLineMatch'
      ) {
        continue;
      }
      for (const column of placement.config.columns) ids.add(column.id);
    }
  }
  return ids;
}

function replaceRegion(
  view: TaskTypeView,
  regionId: string,
  region: TaskTypeView['regions'][number],
): TaskTypeView {
  return {
    ...view,
    regions: view.regions.map((current) =>
      current.id === regionId ? region : current,
    ),
  };
}

function mapRegionPlacements(
  view: TaskTypeView,
  regionId: string,
  update: (
    placements: TaskTypeView['regions'][number]['placements'],
  ) => TaskTypeView['regions'][number]['placements'],
): TaskTypeView {
  const region = view.regions.find((item) => item.id === regionId);
  if (!region) return view;
  const placements = update(region.placements);
  if (placements === region.placements) return view;
  return replaceRegion(view, regionId, { ...region, placements });
}

function mapPlacement(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  update: (placement: TaskTypeViewPlacement) => TaskTypeViewPlacement,
): TaskTypeView {
  return mapRegionPlacements(view, regionId, (placements) =>
    updateById(placements, placementId, update),
  );
}

function findPlacement(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
): TaskTypeViewPlacement | null {
  const region = view.regions.find((item) => item.id === regionId);
  return region?.placements.find((item) => item.id === placementId) ?? null;
}

function mapColumns(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  update: (
    columns: Extract<
      TaskTypeViewPlacement,
      { component: 'lineList' }
    >['config']['columns'],
  ) => Extract<
    TaskTypeViewPlacement,
    { component: 'lineList' }
  >['config']['columns'],
): TaskTypeView {
  return mapPlacement(view, regionId, placementId, (placement) => {
    if (placement.component !== 'lineList') return placement;
    const columns = update(placement.config.columns);
    if (columns === placement.config.columns) return placement;
    return { ...placement, config: { ...placement.config, columns } };
  });
}

function mapColumn(
  view: TaskTypeView,
  regionId: string,
  placementId: string,
  columnId: string,
  update: (column: ViewColumn) => ViewColumn,
): TaskTypeView {
  return mapColumns(view, regionId, placementId, (columns) =>
    updateById(columns, columnId, update),
  );
}

function createColumn(
  view: TaskTypeView,
  component: ColumnKind,
  path: readonly string[],
): ViewColumn {
  const nextPath = [...path];
  if (component === 'match') {
    return {
      id: nextViewId(view, 'match'),
      component: 'match',
      config: {
        label: null,
        path: nextPath,
        target: 'supplier',
        mode: 'edit',
      },
    };
  }
  return {
    id: nextViewId(view, 'field'),
    component: 'field',
    config: { label: null, path: nextPath, mode: 'edit' },
  };
}

function withColumnPath(
  column: ViewColumn,
  path: readonly string[],
): ViewColumn {
  const next = [...path];
  if (column.component === 'match') {
    return { ...column, config: { ...column.config, path: next } };
  }
  return { ...column, config: { ...column.config, path: next } };
}

function withColumnLabel(column: ViewColumn, label: string | null): ViewColumn {
  if (column.component === 'match') {
    return { ...column, config: { ...column.config, label } };
  }
  return { ...column, config: { ...column.config, label } };
}

function withColumnMode(column: ViewColumn, mode: 'read' | 'edit'): ViewColumn {
  if (column.component === 'match') {
    return { ...column, config: { ...column.config, mode } };
  }
  return { ...column, config: { ...column.config, mode } };
}

function withPath(
  placement: TaskTypeViewPlacement,
  path: readonly string[],
): TaskTypeViewPlacement {
  const next = [...path];
  switch (placement.component) {
    case 'field':
      return { ...placement, config: { ...placement.config, path: next } };
    case 'match':
      return { ...placement, config: { ...placement.config, path: next } };
    case 'lineList':
      return { ...placement, config: { ...placement.config, path: next } };
    case 'supplierLineMatch':
      return { ...placement, config: { ...placement.config, path: next } };
    case 'email':
    case 'document':
    case 'apInvoice':
    case 'cashReceipt':
      return placement;
  }
}

function withLabel(
  placement: TaskTypeViewPlacement,
  label: string | null,
): TaskTypeViewPlacement {
  switch (placement.component) {
    case 'field':
      return { ...placement, config: { ...placement.config, label } };
    case 'match':
      return { ...placement, config: { ...placement.config, label } };
    case 'lineList':
      return { ...placement, config: { ...placement.config, label } };
    case 'supplierLineMatch':
      return { ...placement, config: { ...placement.config, label } };
    case 'email':
    case 'document':
    case 'apInvoice':
    case 'cashReceipt':
      return { ...placement, config: { ...placement.config, label } };
  }
}

function withMode(
  placement: TaskTypeViewPlacement,
  mode: 'read' | 'edit',
): TaskTypeViewPlacement {
  switch (placement.component) {
    case 'field':
      return { ...placement, config: { ...placement.config, mode } };
    case 'match':
      return { ...placement, config: { ...placement.config, mode } };
    case 'lineList':
    case 'supplierLineMatch':
    case 'email':
    case 'document':
    case 'apInvoice':
    case 'cashReceipt':
      return placement;
  }
}

/** Same array reference when nothing has that id. */
function removeById<T extends { id: string }>(items: T[], id: string): T[] {
  if (!items.some((item) => item.id === id)) return items;
  return items.filter((item) => item.id !== id);
}

/** Same array reference when the updater returns the item unchanged. */
function updateById<T extends { id: string }>(
  items: T[],
  id: string,
  update: (item: T) => T,
): T[] {
  let changed = false;
  const next = items.map((item) => {
    if (item.id !== id) return item;
    const updated = update(item);
    if (updated !== item) changed = true;
    return updated;
  });
  return changed ? next : items;
}

function moveById<T extends { id: string }>(
  items: readonly T[],
  id: string,
  direction: ViewMoveDirection,
): T[] | null {
  const index = items.findIndex((item) => item.id === id);
  const next = index + direction;
  if (index < 0 || next < 0 || next >= items.length) return null;
  const copy = items.slice();
  const [item] = copy.splice(index, 1);
  if (!item) return null;
  copy.splice(next, 0, item);
  return copy;
}

function samePath(left: readonly string[], right: readonly string[]): boolean {
  return (
    left.length === right.length &&
    left.every((segment, index) => segment === right[index])
  );
}

function isViewPath(path: readonly string[]): boolean {
  return (
    path.length >= 1 &&
    path.length <= 6 &&
    path.every((segment) => segment.trim().length > 0 && segment.length <= 100)
  );
}

function normalizeOptionalText(value: string): string | null {
  if (value === '') return null;
  return value.slice(0, 500);
}
